import { Calculator, Pencil, Palette, Image, Ruler, Workflow, FolderDown, Group, BugPlay, LayoutDashboard, LayoutGrid, Network, Layers, Box, Receipt, Download, Copy, Grid, FileSpreadsheet, Square, RotateCw, Maximize2, EyeOff, Trash2, RefreshCw, CopyPlus, X, Wrench, Undo2 } from 'lucide-react';
import type { ToolDefinition } from '../components/ToolsMenu/types/tools';

/**
 * Registry de ferramentas disponíveis no Tools Menu
 *
 * Extraído para src/config/ (chunk "core") para evitar dependências
 * circulares entre feature chunks (tools ↔ admin).
 *
 * IMPORTANTE para atalhos de teclado:
 * - Ferramentas com 'opensModalId': o atalho usa actionId='abrir-modal'
 * - Ferramentas com 'actionId': o atalho usa esse actionId específico
 * - O executor correspondente deve ser registrado em ToolsMenu.tsx (useEffect de executores)
 */
export const toolsRegistry: ToolDefinition[] = [
  // --- Montagem (nesting) ---
  { id: 'cmd_montatec', name: 'MontaTec', icon: LayoutDashboard, category: 'nesting', actionId: 'command:1' as any },
  { id: 'cmd_mult_montatec', name: 'Mult + MontaTec', icon: LayoutGrid, category: 'nesting', actionId: 'command:2' as any },
  { id: 'montatech', name: 'MontaTech v2', icon: Layers, category: 'nesting', opensModalId: 'modal:montatech' },
  { id: 'montatech-native', name: 'MontaTech 2.0', icon: Layers, category: 'nesting', actionId: 'montatech-native' },

  // --- Transformacao (transform) ---
  { id: 'resize', name: 'Resize', icon: Maximize2, category: 'transform', actionId: 'command:5' as any },
  { id: 'resize-v2', name: 'Resize v2', icon: Maximize2, category: 'transform', opensModalId: 'modal:oneresize' },
  { id: 'girar-objeto', name: 'Girar 45°', icon: RotateCw, category: 'transform', actionId: 'girar-objeto' },
  { id: 'multiplicador-objetos', name: 'Multiplicar', icon: Copy, category: 'transform', actionId: 'multiplicador-objetos' },
  { id: 'multiplicar-objeto-v2', name: 'Multiplicar v2.0', icon: CopyPlus, category: 'transform', actionId: 'multiplicar-objeto-v2' },
  { id: 'grid-objetos', name: 'Organizar', icon: Grid, category: 'transform', actionId: 'grid-objetos' },
  { id: 'layout-grid', name: 'Layout Grid', icon: LayoutGrid, category: 'transform', actionId: 'layout-grid' },
  { id: 'reverter-pipeline', name: 'Reverter Organização', icon: Undo2, category: 'transform', actionId: 'reverter-pipeline' },

  // --- Medicao (measure) ---
  { id: 'oneregua', name: 'OneRégua', icon: Ruler, category: 'measure', opensModalId: 'modal:oneregua' },

  // --- Efeitos (effects) ---
  { id: 'onecontour', name: 'OneContorno', icon: Pencil, category: 'effects', actionId: 'command:4' as any },
  { id: 'onecontour-v2', name: 'OneContorno v2', icon: Pencil, category: 'effects', opensModalId: 'modal:onecontorno' },
  { id: 'transparencia', name: 'Transparencia', icon: EyeOff, category: 'effects', actionId: 'command:6' as any },
  { id: 'contracao-dtf-textil', name: 'Contração DTF Têxtil', icon: Square, category: 'effects', actionId: 'contracao-dtf-textil' },
  { id: 'contracao-dtf-uv', name: 'Contração DTF UV', icon: Square, category: 'effects', actionId: 'contracao-dtf-uv' },

  // --- Exportacao (export) ---
  { id: 'export_batch_local', name: 'Exportar em Lote (Local)', icon: Download, category: 'export', actionId: 'export_batch_local' },
  { id: 'export_producao_offline', name: 'Exportar Produção (Offline)', icon: FolderDown, category: 'export', actionId: 'export_producao_offline' },
  { id: 'repair-pdf', name: 'Reparar PDF', icon: Wrench, category: 'export', actionId: 'repair-pdf' },
  { id: 'auto-bitmap', name: 'Auto Bitmap', icon: Image, category: 'export', actionId: 'auto-bitmap' },
  { id: 'onebitmapv1', name: 'OneBitmap', icon: Image, category: 'export', opensModalId: 'modal:onebitmapv1' },

  // --- Comercial (commercial) ---
  { id: 'quotation-calculator', name: 'Calcular Orçamento', icon: FileSpreadsheet, category: 'commercial', actionId: 'quotation-calculator' },

  // --- Criacao (create) ---
  { id: 'moldesone', name: 'MoldesOne (Legado)', icon: Box, category: 'create', actionId: 'command:3' as any },
  { id: 'moldeone-v2', name: 'MoldeOne v3', icon: Box, category: 'create', opensModalId: 'modal:moldeone' },
  { id: 'criar-retangulo', name: 'Folha A3 UV', icon: Square, category: 'create', actionId: 'criar-retangulo' },

  // --- Utilitarios (utility) ---
  { id: 'smartimport', name: 'Importação Inteligente', icon: FolderDown, category: 'utility', actionId: 'smartimport' },
  { id: 'open-free-file', name: 'Abrir Arquivo Livre', icon: FolderDown, category: 'utility', actionId: 'open-free-file', requiresPermission: false },
  { id: 'runps', name: 'Run PS', icon: Workflow, category: 'utility', actionId: 'runps' },
  { id: 'oneagrupar', name: 'OneAgrupar', icon: Group, category: 'utility', actionId: 'oneagrupar' },
  { id: 'gerenciar-camadas', name: 'Ger. Camadas', icon: Layers, category: 'utility', actionId: 'gerenciar-camadas' as any },
  { id: 'testsmb', name: 'Testar Conexões', icon: Network, category: 'utility', opensModalId: 'modal:testsmb' },
  { id: 'refresh', name: 'Atualizar Tela', icon: RefreshCw, category: 'utility', actionId: 'refresh' },
  { id: 'limpar-cache', name: 'Limpar Cache', icon: Trash2, category: 'utility', actionId: 'limpar-cache' },
  { id: 'close-document', name: 'Fechar Documento', icon: X, category: 'utility', actionId: 'close-document' },

  // --- Desabilitadas (em desenvolvimento) ---
  { id: 'onebitmap', name: 'OneBitmap', icon: Image, disabled: true },
  { id: 'collectmetadata', name: 'Coletar Metadados', icon: BugPlay, disabled: true },
  { id: 'onecolorcheck', name: 'OneColorCheck', icon: Palette, disabled: true },
  { id: 'onecalc', name: 'OneCalc', icon: Calculator, disabled: true },
];
